import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import CountrySearchSelect from '../../common/CountrySearchSelect';
import { useCountries } from '../../../hooks/useCountries';
import { apiGet, apiPost } from '../../../services/api';
import {
  APPLICATION_METHODS,
  countWords,
  EMPLOYMENT_TYPES,
  LOCATION_OPTIONS,
  LOCATION_SPECIFIC_COUNTRY,
  SALARY_CURRENCIES,
  SALARY_PERIODS,
  SENIORITY_OPTIONS,
  seniorityFromSelection,
  seniorityToSelection,
} from '../wizard/wizardConstants';

function locationRadioTestId(value) {
  return `rwm-edit-job-location-${value.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '')}`;
}

function buildInitialForm(job) {
  return {
    title: job?.title || '',
    category_id: job?.category_id ? String(job.category_id) : '',
    employment_type: job?.employment_type || 'full-time',
    seniority: seniorityToSelection(job?.seniority_level),
    salary_min: job?.salary_min != null ? String(job.salary_min) : '',
    salary_max: job?.salary_max != null ? String(job.salary_max) : '',
    salary_currency: job?.salary_currency || 'EUR',
    salary_period: job?.salary_period || 'yearly',
    location_eligibility: job?.location_country ? '' : job?.location_eligibility || '',
    location_country: job?.location_country || '',
    timezone_requirement: job?.timezone_requirement || '',
    description: job?.description || '',
    application_method: job?.application_method || 'rwm_flow',
    application_url: job?.application_url || '',
    application_email: job?.application_email || '',
  };
}

export default function EditJobModal({ jobId, token, onClose, onSaved, handleAuthError }) {
  const { countries, loading: countriesLoading } = useCountries();
  const [job, setJob] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(buildInitialForm(null));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [topError, setTopError] = useState('');
  const [success, setSuccess] = useState(false);

  const loadData = useCallback(async () => {
    if (!token || !jobId) return;
    setLoading(true);
    setTopError('');
    try {
      const [jobRes, categoriesRes] = await Promise.all([
        apiGet(`/api/v1/company/jobs/${jobId}`, token),
        apiGet('/api/v1/categories', token),
      ]);
      if (handleAuthError(jobRes) || handleAuthError(categoriesRes)) return;
      if (!jobRes.ok) {
        setTopError('Could not load job details.');
        return;
      }
      const jobJson = await jobRes.json();
      const loadedJob = jobJson.data ?? null;
      setJob(loadedJob);
      setForm(buildInitialForm(loadedJob));
      if (categoriesRes.ok) {
        const catJson = await categoriesRes.json();
        setCategories(catJson.data ?? []);
      }
    } catch {
      setTopError('Could not load job details.');
    } finally {
      setLoading(false);
    }
  }, [jobId, token, handleAuthError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateSeniority = (value, checked) => {
    setForm((prev) => ({
      ...prev,
      seniority: { ...prev.seniority, [value]: checked },
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.seniority_level;
      return next;
    });
  };

  const locationMode = form.location_country
    ? LOCATION_SPECIFIC_COUNTRY
    : form.location_eligibility || '';

  const sanitizedPreview = useMemo(
    () => DOMPurify.sanitize(form.description || ''),
    [form.description],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !jobId) return;
    setSubmitting(true);
    setTopError('');
    setFieldErrors({});
    setSuccess(false);

    const body = {
      title: form.title.trim(),
      category_id: Number(form.category_id),
      employment_type: form.employment_type,
      seniority_level: seniorityFromSelection(form.seniority),
      salary_min: Number(form.salary_min),
      salary_max: Number(form.salary_max),
      salary_currency: form.salary_currency,
      salary_period: form.salary_period,
      timezone_requirement: form.timezone_requirement || null,
      description: form.description,
      application_method: form.application_method,
      application_url: form.application_method === 'external_url' ? form.application_url : null,
      application_email: form.application_method === 'email' ? form.application_email : null,
    };

    if (form.location_country) {
      body.location_country = form.location_country;
      body.location_eligibility = null;
    } else {
      body.location_eligibility = form.location_eligibility;
      body.location_country = null;
    }

    try {
      const res = await apiPost(`/api/v1/company/jobs/${jobId}/edit`, body, token);
      if (handleAuthError(res)) return;
      if (res.status === 422) {
        const data = await res.json();
        const errors = data?.errors || {};
        const mapped = {};
        Object.keys(errors).forEach((key) => {
          mapped[key] = Array.isArray(errors[key]) ? errors[key][0] : errors[key];
        });
        setFieldErrors(mapped);
        return;
      }
      if (!res.ok) {
        setTopError('Could not submit edits. Please try again.');
        return;
      }
      setSuccess(true);
      window.setTimeout(() => {
        onSaved();
      }, 600);
    } catch {
      setTopError('Could not submit edits. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const descriptionWordCount = countWords(form.description);

  return (
    <Modal show onHide={onClose} size="lg" centered data-testid="rwm-edit-job-modal">
      <Modal.Header closeButton>
        <Modal.Title>Edit Job: {job?.title || '…'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="rwm-edit-job-modal__body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : null}
          {!loading && topError ? (
            <Alert variant="danger" data-testid="rwm-edit-job-error">
              {topError}
            </Alert>
          ) : null}
          {success ? (
            <Alert variant="success" data-testid="rwm-edit-job-success">
              Your edits have been submitted for review.
            </Alert>
          ) : null}
          {!loading && job ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="rwm-edit-job-title">Job title</Form.Label>
                <Form.Control
                  id="rwm-edit-job-title"
                  value={form.title}
                  onChange={(e) => updateForm('title', e.target.value)}
                  isInvalid={Boolean(fieldErrors.title)}
                  data-testid="rwm-edit-job-title"
                  required
                />
                <Form.Control.Feedback type="invalid">{fieldErrors.title}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="rwm-edit-job-category">Category</Form.Label>
                <Form.Select
                  id="rwm-edit-job-category"
                  value={form.category_id}
                  onChange={(e) => updateForm('category_id', e.target.value)}
                  isInvalid={Boolean(fieldErrors.category_id)}
                  data-testid="rwm-edit-job-category"
                  required
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{fieldErrors.category_id}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="rwm-edit-job-employment">Employment type</Form.Label>
                <Form.Select
                  id="rwm-edit-job-employment"
                  value={form.employment_type}
                  onChange={(e) => updateForm('employment_type', e.target.value)}
                  isInvalid={Boolean(fieldErrors.employment_type)}
                  required
                >
                  {EMPLOYMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{fieldErrors.employment_type}</Form.Control.Feedback>
              </Form.Group>

              <fieldset className="mb-3">
                <legend className="form-label fs-6">Seniority</legend>
                {SENIORITY_OPTIONS.map((opt) => (
                  <Form.Check
                    key={opt.value}
                    type="checkbox"
                    id={`rwm-edit-job-seniority-${opt.value}`}
                    label={opt.label}
                    checked={Boolean(form.seniority[opt.value])}
                    onChange={(e) => updateSeniority(opt.value, e.target.checked)}
                    data-testid={`rwm-edit-job-seniority-${opt.value}`}
                  />
                ))}
                {fieldErrors.seniority_level ? (
                  <div className="text-danger small mt-1">{fieldErrors.seniority_level}</div>
                ) : null}
              </fieldset>

              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label htmlFor="rwm-edit-job-salary-min">Minimum salary</Form.Label>
                    <Form.Control
                      id="rwm-edit-job-salary-min"
                      type="number"
                      min="0"
                      value={form.salary_min}
                      onChange={(e) => updateForm('salary_min', e.target.value)}
                      isInvalid={Boolean(fieldErrors.salary_min)}
                      data-testid="rwm-edit-job-salary-min"
                      required
                    />
                    <Form.Control.Feedback type="invalid">{fieldErrors.salary_min}</Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label htmlFor="rwm-edit-job-salary-max">Maximum salary</Form.Label>
                    <Form.Control
                      id="rwm-edit-job-salary-max"
                      type="number"
                      min="0"
                      value={form.salary_max}
                      onChange={(e) => updateForm('salary_max', e.target.value)}
                      isInvalid={Boolean(fieldErrors.salary_max)}
                      data-testid="rwm-edit-job-salary-max"
                      required
                    />
                    <Form.Control.Feedback type="invalid">{fieldErrors.salary_max}</Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="col-md-2">
                  <Form.Group>
                    <Form.Label>Currency</Form.Label>
                    <Form.Select
                      value={form.salary_currency}
                      onChange={(e) => updateForm('salary_currency', e.target.value)}
                    >
                      {SALARY_CURRENCIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-2">
                  <Form.Group>
                    <Form.Label>Period</Form.Label>
                    <Form.Select
                      value={form.salary_period}
                      onChange={(e) => updateForm('salary_period', e.target.value)}
                    >
                      {SALARY_PERIODS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>

              <fieldset className="mb-3">
                <legend className="form-label fs-6">Location</legend>
                {LOCATION_OPTIONS.map((region) => (
                  <Form.Check
                    key={region}
                    type="radio"
                    name="edit-location"
                    id={locationRadioTestId(region)}
                    label={region}
                    checked={locationMode === region}
                    onChange={() => {
                      updateForm('location_eligibility', region);
                      updateForm('location_country', '');
                    }}
                    data-testid={locationRadioTestId(region)}
                  />
                ))}
                <Form.Check
                  type="radio"
                  name="edit-location"
                  id="rwm-edit-job-location-specific-country"
                  label="Specific country"
                  checked={locationMode === LOCATION_SPECIFIC_COUNTRY}
                  onChange={() => updateForm('location_eligibility', '')}
                  data-testid="rwm-edit-job-location-specific-country"
                />
                {locationMode === LOCATION_SPECIFIC_COUNTRY ? (
                  <div className="mt-2">
                    <CountrySearchSelect
                      countries={countries}
                      loading={countriesLoading}
                      value={form.location_country}
                      onChange={(name) => updateForm('location_country', name)}
                      testId="rwm-edit-job-country"
                    />
                  </div>
                ) : null}
                {fieldErrors.location_eligibility || fieldErrors.location_country || fieldErrors.location ? (
                  <div className="text-danger small mt-1">
                    {fieldErrors.location || fieldErrors.location_eligibility || fieldErrors.location_country}
                  </div>
                ) : null}
              </fieldset>

              <Form.Group className="mb-3">
                <Form.Label htmlFor="rwm-edit-job-description">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  id="rwm-edit-job-description"
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  isInvalid={Boolean(fieldErrors.description)}
                  data-testid="rwm-edit-job-description"
                  required
                />
                <Form.Text className={descriptionWordCount < 100 ? 'text-danger' : 'text-muted'}>
                  {descriptionWordCount} / 100 words minimum
                </Form.Text>
                <Form.Control.Feedback type="invalid">{fieldErrors.description}</Form.Control.Feedback>
                {form.description ? (
                  <div
                    className="border rounded p-3 mt-2 small rwm-edit-job-modal__preview"
                    dangerouslySetInnerHTML={{ __html: sanitizedPreview }}
                  />
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Application method</Form.Label>
                {APPLICATION_METHODS.map((method) => (
                  <Form.Check
                    key={method.value}
                    type="radio"
                    name="edit-application-method"
                    id={`rwm-edit-job-method-${method.value}`}
                    label={method.label}
                    checked={form.application_method === method.value}
                    onChange={() => updateForm('application_method', method.value)}
                    data-testid={`rwm-edit-job-method-${method.value}`}
                  />
                ))}
              </Form.Group>

              {form.application_method === 'external_url' ? (
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="rwm-edit-job-url">Application URL</Form.Label>
                  <Form.Control
                    id="rwm-edit-job-url"
                    type="url"
                    value={form.application_url}
                    onChange={(e) => updateForm('application_url', e.target.value)}
                    isInvalid={Boolean(fieldErrors.application_url)}
                  />
                  <Form.Control.Feedback type="invalid">{fieldErrors.application_url}</Form.Control.Feedback>
                </Form.Group>
              ) : null}

              {form.application_method === 'email' ? (
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="rwm-edit-job-email">Application email</Form.Label>
                  <Form.Control
                    id="rwm-edit-job-email"
                    type="email"
                    value={form.application_email}
                    onChange={(e) => updateForm('application_email', e.target.value)}
                    isInvalid={Boolean(fieldErrors.application_email)}
                  />
                  <Form.Control.Feedback type="invalid">{fieldErrors.application_email}</Form.Control.Feedback>
                </Form.Group>
              ) : null}

              {fieldErrors.edit_window ? (
                <Alert variant="warning">{fieldErrors.edit_window}</Alert>
              ) : null}
              {fieldErrors.status ? (
                <Alert variant="warning">{fieldErrors.status}</Alert>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onClose}
            disabled={submitting}
            data-testid="rwm-edit-job-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="info"
            disabled={submitting || loading || success}
            data-testid="rwm-edit-job-submit"
          >
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
